// The homepage ships as static HTML. This script only adjusts what depends on the visitor's screen: zoomed figures
// shrink to fit, size readouts report what the browser actually drew, and the app icon whisks when clicked.

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function contentWidth(element: HTMLElement): number {
  const style = getComputedStyle(element);
  return element.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
}

function setZoom(element: HTMLElement, zoom: number) {
  element.style.setProperty('--z', zoom.toFixed(3));
}

// Artboards are drawn at their real width and zoomed, so a narrow screen lowers the zoom rather than cropping them.
function fitFigures() {
  for (const element of document.querySelectorAll<HTMLElement>('[data-fit-width]')) {
    // A figure sized by its own content measures the box it should fit in instead of its parent.
    const box = element.closest<HTMLElement>('[data-fit-box]') ?? element.parentElement;
    if (!box) continue;
    const natural = Number(element.dataset['fitWidth']);
    const largest = Number(element.dataset['fitMax'] ?? 1);
    setZoom(element, Math.min(largest, contentWidth(box) / natural));
  }
  // The hero canvas centres two phone artboards with a 40px gap and some breathing room either side.
  for (const element of document.querySelectorAll<HTMLElement>('[data-canvas-fit]')) {
    const canvas = element.parentElement;
    if (!canvas) continue;
    setZoom(element, Math.max(0.3, Math.min(0.6, (canvas.clientWidth - 48) / 820)));
  }
}

function measureSelections() {
  for (const readout of document.querySelectorAll<HTMLElement>('[data-readout]')) {
    const host = readout.parentElement?.parentElement;
    if (!host || host.getClientRects().length === 0) continue;
    const zoom = parseFloat(getComputedStyle(host).getPropertyValue('--z')) || 1;
    const box = host.getBoundingClientRect();
    readout.textContent = `${format(box.width / zoom)} × ${format(box.height / zoom)}`;
  }
}

function format(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

// A quick squash to wind up, two fast stirs that tighten as they go, then a pop and a soft landing.
function whisk(icon: HTMLElement) {
  if (reduceMotion) return;
  const frames: Keyframe[] = [
    {
      offset: 0,
      transform: 'translate(0, 0) rotate(0deg) scale(1, 1)',
      easing: 'cubic-bezier(0.3, 0, 0.6, 1)',
    },
    { offset: 0.14, transform: 'translate(0, 6%) rotate(-10deg) scale(1.1, 0.88)' },
  ];
  const steps = 16;
  for (let step = 1; step <= steps; step++) {
    const angle = (step / 8) * 2 * Math.PI;
    const radius = 6 * (1 - (0.35 * step) / steps);
    const cos = Math.cos(angle);
    frames.push({
      offset: 0.14 + (0.58 * step) / steps,
      transform: `translate(${(-radius * Math.sin(angle)).toFixed(2)}%, ${(radius * cos).toFixed(2)}%) rotate(${(-10 * cos).toFixed(1)}deg) scale(${(1 + 0.05 * cos).toFixed(3)}, ${(1 - 0.05 * cos).toFixed(3)})`,
      easing: step === steps ? 'cubic-bezier(0.2, 0.8, 0.3, 1)' : 'linear',
    });
  }
  frames.push(
    {
      offset: 0.84,
      transform: 'translate(0, -10%) rotate(4deg) scale(0.94, 1.08)',
      easing: 'cubic-bezier(0.5, 0, 0.8, 0.6)',
    },
    {
      offset: 0.93,
      transform: 'translate(0, 1%) rotate(-2deg) scale(1.04, 0.97)',
      easing: 'ease-out',
    },
    { offset: 1, transform: 'translate(0, 0) rotate(0deg) scale(1, 1)' },
  );
  icon.animate(frames, { duration: 620 });
}

function refresh() {
  fitFigures();
  measureSelections();
}

for (const icon of document.querySelectorAll<HTMLElement>('[data-whisk]')) {
  icon.addEventListener('click', () => whisk(icon));
}

refresh();
window.addEventListener('resize', refresh);
document.fonts.ready.then(refresh, () => undefined);
